// INV34 (docs/build-briefs/INV34-cost-and-margin.md; DEC-291; design/mockups/
// inv-report-cost-margin.html): the body of the Cost and margin report, drawn
// inside the Reports group's own frame (ui/src/reports.ts owns the title, the
// range chips, the custom dates and the freshness strip). This module only
// draws what the server sent and reports every tap back through `handlers`.
//
// Admin surface, touch-sized: every control is a 44px button, nothing depends
// on hover, nothing is in a tooltip, and there is no keyboard listener. Text is
// set with textContent only (drink names are user-entered). It reuses the Usage
// and waste styles (the `uw` root class) and adds a few of its own (`cm`).

import type { CostMarginDrinkDto, CostMarginReportDto } from "./api.ts";
import {
  categoryName,
  drinkTags,
  leftOutNotice,
  partLine,
  partialNotice,
  drinkPercentText,
  percentText,
  plainWords,
  whyLeftOut,
} from "./costMarginModel.ts";
import { dayLabel, formatMoney } from "./reportFormat.ts";

export const DRINK_ROWS_SHOWN = 20;
export const LIST_ROWS_SHOWN = 10;
export const DAY_ROWS_SHOWN = 14;

export type CostMarginFlag =
  "onlyBelow" | "onlyNoCost" | "onlyPartial" | "onlyRemade" | "onlyDiscounts";
export type CostMarginSortChoice = "worst" | "low" | "sold" | "sales" | "name";

export interface CostMarginFilterState {
  category: string;
  onlyBelow: boolean;
  onlyNoCost: boolean;
  onlyPartial: boolean;
  onlyRemade: boolean;
  onlyDiscounts: boolean;
  sort: CostMarginSortChoice;
}

export interface CostMarginView {
  filters: CostMarginFilterState;
  showAll: boolean;
  showAllDays: boolean;
  showAllLists: boolean;
  open: ReadonlySet<string>;
  tieOpen: boolean;
  canExport: boolean;
}

export interface CostMarginHandlers {
  onCategory: (category: string) => void;
  onFlag: (flag: CostMarginFlag) => void;
  onSort: (sort: CostMarginSortChoice) => void;
  onClearFilters: () => void;
  onShowAll: () => void;
  onShowAllDays: () => void;
  onShowAllLists: () => void;
  onToggleOpen: (key: string) => void;
  onToggleTie: () => void;
  onDownloadDays: () => void;
  onShowLastWeek: () => void;
  onPickOtherDates: () => void;
  /** INV35-A1: open Count variance on the dates now on screen. */
}

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) {
    element.setAttribute(key, value);
  }
  for (const child of children) element.append(child);
  return element;
}

function button(text: string, cls: string, testid: string, onClick: () => void): HTMLElement {
  const element = h("button", { cls, text, testid, attrs: { type: "button" } });
  element.addEventListener("click", onClick);
  return element;
}

function chip(
  label: string,
  on: boolean,
  testid: string,
  onClick: () => void,
  count?: number,
): HTMLElement {
  const element = h("button", {
    cls: `chip${on ? " on" : ""}`,
    testid,
    attrs: { type: "button", "aria-pressed": String(on) },
  });
  element.append(label);
  if (count !== undefined) element.append(" ", h("span", { cls: "n", text: String(count) }));
  element.addEventListener("click", onClick);
  return element;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function kpi(
  label: string,
  value: string,
  d: string,
  d2: string,
  testid: string,
  tone = "",
): HTMLElement {
  return h("div", { cls: `kpi${tone === "" ? "" : ` ${tone}`}`, testid }, [
    h("div", { cls: "k", text: label }),
    h("div", { cls: "v", testid: `${testid}-value`, text: value }),
    h("div", { cls: "d", text: d }),
    h("div", { cls: "d2", text: d2 }),
  ]);
}

function notice(
  parts: { bold: string; rest: string },
  testid: string,
  tone: "" | "info" = "",
): HTMLElement {
  return h("div", { cls: `notice${tone === "" ? "" : ` ${tone}`}`, testid }, [
    h("div", {}, [h("b", { text: parts.bold }), parts.rest]),
  ]);
}

function money(cents: number | null): string {
  return cents === null ? "-" : formatMoney(cents);
}

function minus(cents: number): string {
  return cents === 0 ? formatMoney(0) : `-${formatMoney(cents)}`;
}

function drinkKey(d: CostMarginDrinkDto): string {
  return d.menuDrinkId ?? `ref:${d.name}`;
}

export function renderCostMargin(
  host: HTMLElement,
  report: CostMarginReportDto,
  view: CostMarginView,
  handlers: CostMarginHandlers,
): void {
  host.replaceChildren();
  const root = h("div", { cls: "uw cm", testid: "cm-root" });
  host.append(root);
  const { totals, meta } = report;
  const f = view.filters;
  const filtered =
    f.category !== "" ||
    f.onlyBelow ||
    f.onlyNoCost ||
    f.onlyPartial ||
    f.onlyRemade ||
    f.onlyDiscounts;

  if (totals.drinks === 0 && !filtered) {
    root.append(
      h("div", { cls: "card", testid: "cm-empty" }, [
        h("div", { cls: "empty" }, [
          h("h3", { text: "No drinks were sold in these dates" }),
          h("p", {
            text: `Nothing was collected from ${dayLabel(meta.from)}${meta.from === meta.to ? "" : ` to ${dayLabel(meta.to)}`}.`,
          }),
          h("div", { cls: "inline-cta" }, [
            button("Show last week", "btn-secondary", "cm-show-last-week", handlers.onShowLastWeek),
            button("Pick other dates", "btn-secondary", "cm-pick-dates", handlers.onPickOtherDates),
          ]),
        ]),
      ]),
    );
    return;
  }

  // ---- notices ----
  const noCostAtAll = totals.costedDrinks === 0;
  if (noCostAtAll) {
    root.append(
      notice(
        {
          bold: "No drink has a cost yet. ",
          rest:
            "Starting stock carries no cost, so costs begin with your first delivery that has a price. " +
            "Until then this report shows sales only. Enter the price on a delivery in Record an outside purchase and the drinks made from it get a cost from then on.",
        },
        "cm-notice-nocost",
      ),
    );
  } else {
    const left = leftOutNotice(totals.left);
    if (left !== null) root.append(notice(left, "cm-notice-left"));
    const partial = partialNotice(totals.partlyCostedDrinks);
    if (partial !== null) root.append(notice(partial, "cm-notice-partial"));
  }
  if (totals.untrackedDrinks > 0) {
    const names = totals.untrackedPartNames;
    root.append(
      notice(
        {
          bold: `${totals.untrackedDrinks} ${plural(totals.untrackedDrinks, "drink uses", "drinks use")} a recipe part that is not tracked. `,
          rest: `${names.join(" and ")} ${names.length === 1 ? "is" : "are"} not in the cost, so the cost shown is a little low. Link the part to an item in Settings to count it.`,
        },
        "cm-notice-untracked",
        "info",
      ),
    );
  }
  const tie = report.actualVsTheoretical.tie;
  if (meta.open && tie.madeNotPaidLines > 0) {
    root.append(
      notice(
        {
          bold: `${tie.madeNotPaidLines} ${plural(tie.madeNotPaidLines, "drink is", "drinks are")} made and not paid yet. `,
          rest: `Their cost is ${formatMoney(tie.madeNotPaidCents)}. It shows here once the tab is paid.`,
        },
        "cm-notice-unpaid",
        "info",
      ),
    );
  }

  // ---- cards ----
  const discountsTaken = totals.discountCents + totals.compCents + totals.memberBenefitCents;
  const cards: HTMLElement[] = [
    kpi(
      "Sales",
      formatMoney(totals.salesCents),
      `${totals.sold} ${plural(totals.sold, "drink", "drinks")} sold`,
      `Discounts, comps and member benefits took off ${formatMoney(discountsTaken)}; refunds ${formatMoney(totals.refundCents)}`,
      "cm-kpi-sales",
    ),
  ];
  if (noCostAtAll) {
    cards.push(
      kpi(
        "Cost of drinks sold",
        "No cost yet",
        "Needs a delivery with a price",
        "",
        "cm-kpi-cost",
        "warn",
      ),
      kpi("Gross margin", "-", "Waits for a cost", "", "cm-kpi-margin", "warn"),
      kpi("Pour cost", "-", "Waits for a cost", "", "cm-kpi-pour", "warn"),
    );
  } else {
    cards.push(
      kpi(
        "Cost of drinks sold",
        formatMoney(totals.costCents),
        `${totals.costedDrinks} of ${totals.drinks} drinks have a cost`,
        `Includes ${formatMoney(totals.remakeCostCents)} for remakes`,
        "cm-kpi-cost",
      ),
      kpi(
        "Gross margin",
        formatMoney(totals.marginCents),
        `${percentText(totals.marginBp)} of sales that have a cost`,
        `Sales with a cost: ${formatMoney(totals.costedSalesCents)}`,
        "cm-kpi-margin",
        totals.marginCents < 0 ? "bad" : "",
      ),
      kpi(
        "Pour cost",
        percentText(totals.pourCostBp),
        totals.pourCostBp === null
          ? "Nothing to divide by"
          : `${Math.round(totals.pourCostBp / 100)} cents of every sales dollar`,
        "Lower is better. Cost divided by sales",
        "cm-kpi-pour",
      ),
    );
  }
  root.append(h("div", { cls: "kpis", testid: "cm-kpis" }, cards));
  if (totals.remakes > 0) {
    root.append(
      h("div", { cls: "strip", testid: "cm-remakes" }, [
        h("span", {}, [
          h("b", { text: "Remakes: " }),
          `${totals.remakes} ${plural(totals.remakes, "drink was", "drinks were")} made again. That added ${formatMoney(totals.remakeCostCents)} of cost and no sales, and is already inside Cost above. Item usage still counts a remake as Used.`,
        ]),
      ]),
    );
  }

  // ---- by category ----
  const catBody = h("tbody");
  for (const c of report.byCategory) {
    const on = f.category === c.category;
    const row = h(
      "tr",
      {
        cls: `cat${on ? " on" : ""}`,
        testid: "cm-cat-row",
        attrs: { "data-category": c.category, "aria-pressed": String(on) },
      },
      [
        h("td", { cls: "l", text: categoryName(c.category) }),
        h("td", { text: String(c.drinks) }),
        h("td", { text: String(c.sold) }),
        h("td", { cls: "strong", text: formatMoney(c.salesCents) }),
        h("td", {
          text: c.costedSalesCents > 0 || c.costCents > 0 ? formatMoney(c.costCents) : "-",
        }),
        h("td", { text: c.costedSalesCents > 0 ? formatMoney(c.marginCents) : "-" }),
        h("td", { text: percentText(c.marginBp) }),
        h("td", { text: percentText(c.pourCostBp) }),
      ],
    );
    row.addEventListener("click", () => handlers.onCategory(on ? "" : c.category));
    catBody.append(row);
  }
  catBody.append(
    h("tr", { cls: "total" }, [
      h("td", { cls: "l", text: "Total" }),
      h("td", { text: String(totals.drinks) }),
      h("td", { text: String(totals.sold) }),
      h("td", { text: formatMoney(totals.salesCents) }),
      h("td", { text: noCostAtAll ? "-" : formatMoney(totals.costCents) }),
      h("td", { text: noCostAtAll ? "-" : formatMoney(totals.marginCents) }),
      h("td", { text: percentText(totals.marginBp) }),
      h("td", { text: percentText(totals.pourCostBp) }),
    ]),
  );
  root.append(
    h("div", { cls: "card", testid: "cm-categories" }, [
      h("h2", {}, [
        "By category ",
        h("small", {
          text: "Tap a category to filter the drink list below. The other filters still apply",
        }),
      ]),
      h("div", { cls: "tblwrap" }, [
        h("table", { cls: "cats cm-cats" }, [
          h("thead", {}, [
            h("tr", {}, [
              h("th", { cls: "l", text: "Category" }),
              h("th", { text: "Drinks" }),
              h("th", { text: "Sold" }),
              h("th", { text: "Sales" }),
              h("th", { text: "Cost" }),
              h("th", { text: "Margin" }),
              h("th", { text: "Margin %" }),
              h("th", { text: "Pour cost %" }),
            ]),
          ]),
          catBody,
        ]),
      ]),
      h("div", { cls: "memo" }, [
        h("span", {
          text: "Margin and the two percents use only sales that have a cost. Sales is every sale, so it matches Sales by item.",
        }),
      ]),
    ]),
  );

  // ---- by drink ----
  const drinksCard = h("div", { cls: "card", testid: "cm-drinks" });
  drinksCard.append(
    h("h2", {}, [
      "By drink ",
      h("small", {
        testid: "cm-drink-count",
        text: `${report.drinks.length} ${plural(report.drinks.length, "drink", "drinks")}`,
      }),
    ]),
  );
  const group = (label: string, chips: HTMLElement[]): HTMLElement =>
    h("div", { cls: "fgroup" }, [h("span", { cls: "lab", text: label }), ...chips]);
  drinksCard.append(
    group("Category", [
      chip("All", f.category === "", "cm-cat-all", () => handlers.onCategory("")),
      ...report.byCategory.map((c) =>
        chip(
          categoryName(c.category),
          f.category === c.category,
          "cm-cat-chip",
          () => handlers.onCategory(f.category === c.category ? "" : c.category),
          c.drinks,
        ),
      ),
    ]),
    group("Only show", [
      chip("Below cost", f.onlyBelow, "cm-flag-below", () => handlers.onFlag("onlyBelow")),
      chip("No cost yet", f.onlyNoCost, "cm-flag-nocost", () => handlers.onFlag("onlyNoCost")),
      chip("Partly costed", f.onlyPartial, "cm-flag-partial", () => handlers.onFlag("onlyPartial")),
      chip("Remade", f.onlyRemade, "cm-flag-remade", () => handlers.onFlag("onlyRemade")),
      chip("Had discounts or comps", f.onlyDiscounts, "cm-flag-discounts", () =>
        handlers.onFlag("onlyDiscounts"),
      ),
    ]),
    group("Sort by", [
      chip("Worst margin %", f.sort === "worst", "cm-sort-worst", () => handlers.onSort("worst")),
      chip("Lowest margin $", f.sort === "low", "cm-sort-low", () => handlers.onSort("low")),
      chip("Most sold", f.sort === "sold", "cm-sort-sold", () => handlers.onSort("sold")),
      chip("Most sales", f.sort === "sales", "cm-sort-sales", () => handlers.onSort("sales")),
      chip("Category and name", f.sort === "name", "cm-sort-name", () => handlers.onSort("name")),
    ]),
  );
  if (report.drinks.length === 0) {
    drinksCard.append(
      h("div", { cls: "empty", testid: "cm-no-drinks" }, [
        h("h3", { text: "No drinks match these filters" }),
        h("p", { text: "Clear a filter to see more." }),
        button("Clear all filters", "btn-quiet", "cm-clear-filters", handlers.onClearFilters),
      ]),
    );
  } else {
    const body = h("tbody");
    const shown = report.drinks;
    const sum = (pick: (d: CostMarginDrinkDto) => number): number =>
      shown.reduce((n, d) => n + pick(d), 0);
    const costedShown = shown.filter((d) => d.costCents !== null);
    const sCost = costedShown.reduce((n, d) => n + (d.costCents ?? 0), 0);
    const sCosted = costedShown.reduce((n, d) => n + d.costedSalesCents, 0);
    body.append(
      h("tr", { cls: "total", testid: "cm-total-row" }, [
        h("td", {
          cls: "l sticky item",
          text: `Total, ${shown.length} ${plural(shown.length, "drink", "drinks")}`,
        }),
        h("td", { text: String(sum((d) => d.sold)) }),
        h("td", { text: formatMoney(sum((d) => d.salesCents)) }),
        h("td", { text: costedShown.length > 0 ? formatMoney(sCost) : "-" }),
        h("td", { cls: "muted", text: "-" }),
        h("td", { text: costedShown.length > 0 ? formatMoney(sCosted - sCost) : "-" }),
        h("td", {
          text: percentText(sCosted > 0 ? 10000 - Math.round((sCost * 10000) / sCosted) : null),
        }),
        h("td", { text: percentText(sCosted > 0 ? Math.round((sCost * 10000) / sCosted) : null) }),
      ]),
    );
    const visible = view.showAll ? shown : shown.slice(0, DRINK_ROWS_SHOWN);
    for (const d of visible) {
      const open = view.open.has(drinkKey(d));
      body.append(drinkRow(d, open, handlers));
      if (open) body.append(detailRow(d));
    }
    drinksCard.append(
      h("div", { cls: "tblwrap" }, [
        h("table", { cls: "items cm-items" }, [
          h("thead", {}, [
            h("tr", {}, [
              h("th", { cls: "l sticky item", text: "Drink" }),
              h("th", { text: "Sold" }),
              h("th", { text: "Sales" }),
              h("th", { text: "Cost" }),
              h("th", { text: "Cost a drink" }),
              h("th", { text: "Margin" }),
              h("th", { text: "Margin %" }),
              h("th", { text: "Pour cost %" }),
            ]),
          ]),
          body,
        ]),
      ]),
    );
    if (!view.showAll && shown.length > DRINK_ROWS_SHOWN) {
      drinksCard.append(
        h("div", { cls: "showall" }, [
          button(`Show all ${shown.length} drinks`, "btn-quiet", "cm-show-all", handlers.onShowAll),
        ]),
      );
    }
  }
  drinksCard.append(
    h("div", { cls: "memo" }, [
      h("span", {}, [
        h("b", { text: "Pour cost" }),
        " is cost divided by sales. 22% means 22 cents of every sales dollar went to what was poured. ",
        h("b", { text: "Margin" }),
        " is sales minus cost. The two percents add up to 100%. Tap a drink to see how its sales and cost were worked out.",
      ]),
      h("span", {
        text: "A drink with a part that has no price yet is costed from the parts that do and tagged Partly costed. A drink with no priced part at all sits at the end of a margin sort and shows No cost yet. A drink sold below its cost is tagged Below cost.",
      }),
    ]),
  );
  root.append(drinksCard);

  // ---- by day ----
  root.append(daysCard(report, view, handlers));

  // ---- actual vs theoretical ----
  root.append(actualCard(report, view, handlers));

  // ---- partly costed and left out ----
  if (report.partlyCosted.length > 0) {
    root.append(listCard(report.partlyCosted, view, handlers, "partial"));
  }
  if (report.leftOut.length > 0) {
    root.append(listCard(report.leftOut, view, handlers, "left"));
  }
}

function drinkRow(d: CostMarginDrinkDto, open: boolean, handlers: CostMarginHandlers): HTMLElement {
  const tags = drinkTags(d);
  const label = h("span", { cls: "dlab" }, [
    h("span", { cls: "uw-item-name", text: d.name }),
    h("small", { text: categoryName(d.category) }),
  ]);
  if (tags.length > 0) {
    label.append(
      h(
        "span",
        { cls: "tags", testid: "cm-tags" },
        tags.map((t) => h("span", { cls: `tag ${t.tone}`, text: t.text })),
      ),
    );
  }
  label.append(h("span", { cls: "more", text: open ? "Hide details" : "Show details" }));
  const toggle = h(
    "button",
    {
      cls: "uw-item-button",
      testid: "cm-drink-toggle",
      attrs: { type: "button", "aria-expanded": String(open) },
    },
    [label],
  );
  toggle.addEventListener("click", () => handlers.onToggleOpen(drinkKey(d)));
  const row = h("tr", {
    cls: open ? "open" : "",
    testid: "cm-drink-row",
    attrs: { "data-drink": d.name },
  });
  row.append(
    h("td", { cls: "l sticky item" }, [toggle]),
    h("td", { text: String(d.sold) }),
    h("td", { cls: "strong", text: formatMoney(d.salesCents) }),
  );
  if (d.costCents === null) {
    row.append(
      h("td", { cls: "nc", text: "No cost yet" }),
      h("td", { cls: "muted", text: "-" }),
      h("td", { cls: "muted", text: "-" }),
      h("td", { cls: "muted", text: "-" }),
      h("td", { cls: "muted", text: "-" }),
    );
    return row;
  }
  const bad = d.marginCents !== null && d.marginCents < 0;
  row.append(
    h("td", { text: formatMoney(d.costCents) }),
    h("td", { text: money(d.costPerDrinkCents) }),
    h("td", { cls: bad ? "below" : "strong", text: money(d.marginCents) }),
    h("td", { cls: bad ? "below" : "", text: drinkPercentText(d.marginBp) }),
    h("td", { cls: bad ? "below" : "", text: drinkPercentText(d.pourCostBp) }),
  );
  return row;
}

function detailRow(d: CostMarginDrinkDto): HTMLElement {
  const li = (label: string, value: string, cls = ""): HTMLElement =>
    h("li", { cls }, [h("span", { text: label }), h("b", { text: value })]);
  const soldFor = h("div", {}, [
    h("h4", { text: "What it sold for" }),
    h("ul", {}, [
      li("Rung up at the price on the ticket", formatMoney(d.rungUpCents)),
      li("Discounts", d.discountCents > 0 ? minus(d.discountCents) : formatMoney(0)),
      li("Comps", d.compCents > 0 ? minus(d.compCents) : formatMoney(0)),
      li(
        "Member benefits",
        d.memberBenefitCents > 0 ? minus(d.memberBenefitCents) : formatMoney(0),
      ),
      li("Refunds", d.refundCents > 0 ? minus(d.refundCents) : formatMoney(0)),
      li("Sales, as on Sales by item", formatMoney(d.salesCents), "tot"),
    ]),
  ]);
  if (d.priceChangedLines > 0) {
    soldFor.append(
      h("p", {
        text: `${d.priceChangedLines} ${plural(d.priceChangedLines, "line was", "lines were")} sold at a changed price. The price on the ticket is what counts.`,
      }),
    );
  }
  const cost = h("div", {}, [h("h4", { text: "What it cost" })]);
  if (d.costCents === null) {
    cost.append(
      h("p", {
        text:
          d.state === "noparts"
            ? "This drink has no tracked parts, so nothing leaves stock when it is made and there is no cost to show."
            : `No cost yet on ${d.missingParts.join(", ")}. It gets one at its next delivery with a price. Drinks already sold stay without a cost.`,
      }),
    );
  } else {
    const items = d.parts.map((p) =>
      h("li", {}, [
        h("span", { cls: "dlab" }, [
          p.name,
          h("small", { text: p.priced ? partLine(p) : `${partLine(p)}. No price yet` }),
        ]),
        h("b", { text: p.priced ? formatMoney(p.costCents) : "No price yet" }),
      ]),
    );
    if (d.remakes > 0) {
      items.push(
        li(
          `Includes ${d.remakes} ${plural(d.remakes, "remake", "remakes")}`,
          formatMoney(d.remakeCostCents ?? 0),
        ),
      );
    }
    items.push(li("Cost", formatMoney(d.costCents), "tot"));
    cost.append(h("ul", {}, items));
  }
  const words = h("div", {}, [h("h4", { text: "In plain words" })]);
  for (const line of plainWords(d)) words.append(h("p", { text: line }));
  return h("tr", { cls: "detail", testid: "cm-detail" }, [
    h("td", { attrs: { colspan: "8" } }, [h("div", { cls: "dgrid" }, [soldFor, cost, words])]),
  ]);
}

function daysCard(
  report: CostMarginReportDto,
  view: CostMarginView,
  handlers: CostMarginHandlers,
): HTMLElement {
  const { days, totals, meta } = report;
  const noCostAtAll = totals.costedDrinks === 0;
  const live = days.filter((d) => d.sold !== 0 || d.salesCents !== 0 || d.costCents !== 0);
  let worst: string | null = null;
  let worstBp = -1;
  if (!noCostAtAll) {
    for (const d of live) {
      if (meta.open && d.date === meta.today) continue;
      if (d.pourCostBp !== null && d.pourCostBp > worstBp) {
        worstBp = d.pourCostBp;
        worst = d.date;
      }
    }
  }
  const card = h("div", { cls: "card", testid: "cm-days" }, [
    h("h2", {}, [
      "By day ",
      h("small", { text: "Each sale counts on the day it was paid, with the cost of its drinks" }),
    ]),
  ]);
  card.append(
    h("div", { cls: "legend" }, [
      h("span", {}, [h("span", { cls: "sw sales" }), "Sales"]),
      h("span", {}, [h("span", { cls: "sw cost" }), "Cost (the filled part is the pour cost)"]),
    ]),
  );
  const maxSales = Math.max(1, ...days.map((d) => d.salesCents));
  const many = days.length > 14;
  const chart = h("div", { cls: "chart", attrs: { "aria-hidden": "true" } });
  days.forEach((d, i) => {
    const height = d.salesCents > 0 ? Math.max(4, Math.round((d.salesCents / maxSales) * 130)) : 0;
    const fill =
      d.salesCents > 0 && !noCostAtAll
        ? Math.min(100, Math.round((d.costCents / d.salesCents) * 100))
        : 0;
    const dayNumber = String(Number(d.date.slice(8)));
    const lab = many ? (i % 5 === 0 || i === days.length - 1 ? dayNumber : "") : dayNumber;
    chart.append(
      h("div", { cls: `col${worst === d.date ? " worst" : ""}` }, [
        h("span", {
          cls: "val",
          text: many || height === 0 ? "" : `$${Math.round(d.salesCents / 100)}`,
        }),
        h("div", { cls: "bar", attrs: { style: `height:${height}px` } }, [
          h("i", { attrs: { style: `height:${fill}%` } }),
        ]),
        h("span", { cls: "lab", text: lab }),
      ]),
    );
  });
  card.append(chart);
  const body = h("tbody");
  const shown = view.showAllDays ? days : days.slice(0, DAY_ROWS_SHOWN);
  for (const d of shown) {
    const tags: HTMLElement[] = [];
    if (meta.open && d.date === meta.today) tags.push(h("span", { cls: "tag open", text: "Open" }));
    if (worst === d.date) tags.push(h("span", { cls: "tag low", text: "Highest pour cost" }));
    const nodata = d.salesCents === 0 && d.costCents === 0 && d.sold === 0;
    body.append(
      h("tr", { testid: "cm-day-row", attrs: { "data-date": d.date } }, [
        h("td", { cls: "l" }, [d.date, ...tags.flatMap((t) => [" ", t])]),
        h("td", { text: String(d.sold) }),
        h("td", { cls: "strong", text: formatMoney(d.salesCents) }),
        noCostAtAll
          ? h("td", { cls: "nc", text: "No cost yet" })
          : h("td", { text: nodata ? "-" : formatMoney(d.costCents) }),
        h("td", { text: noCostAtAll || nodata ? "-" : formatMoney(d.marginCents) }),
        h("td", { text: nodata || noCostAtAll ? "-" : drinkPercentText(d.marginBp) }),
        h("td", { text: nodata || noCostAtAll ? "-" : drinkPercentText(d.pourCostBp) }),
      ]),
    );
  }
  body.append(
    h("tr", { cls: "total" }, [
      h("td", { cls: "l", text: "Total" }),
      h("td", { text: String(totals.sold) }),
      h("td", { text: formatMoney(totals.salesCents) }),
      h("td", { text: noCostAtAll ? "-" : formatMoney(totals.costCents) }),
      h("td", { text: noCostAtAll ? "-" : formatMoney(totals.marginCents) }),
      h("td", { text: percentText(totals.marginBp) }),
      h("td", { text: percentText(totals.pourCostBp) }),
    ]),
  );
  card.append(
    h("div", { cls: "tblwrap" }, [
      h("table", { cls: "days cm-days-table" }, [
        h("thead", {}, [
          h("tr", {}, [
            h("th", { cls: "l", text: "Day" }),
            h("th", { text: "Sold" }),
            h("th", { text: "Sales" }),
            h("th", { text: "Cost" }),
            h("th", { text: "Margin" }),
            h("th", { text: "Margin %" }),
            h("th", { text: "Pour cost %" }),
          ]),
        ]),
        body,
      ]),
    ]),
  );
  if (!view.showAllDays && days.length > DAY_ROWS_SHOWN) {
    card.append(
      h("div", { cls: "showall" }, [
        button(
          `Show all ${days.length} days`,
          "btn-quiet",
          "cm-show-all-days",
          handlers.onShowAllDays,
        ),
      ]),
    );
  }
  if (view.canExport) {
    card.append(
      h("div", { cls: "inline-cta cm-left" }, [
        button("Download days CSV", "btn-quiet", "cm-download-days", handlers.onDownloadDays),
      ]),
    );
  }
  card.append(
    h("div", { cls: "memo" }, [
      h("span", {
        text: "The chart is a picture of the table. Every number is in the table. Highest pour cost marks the finished day with the largest pour cost, so a bad night stands out without a target number.",
      }),
    ]),
  );
  return card;
}

function actualCard(
  report: CostMarginReportDto,
  view: CostMarginView,
  handlers: CostMarginHandlers,
): HTMLElement {
  const a = report.actualVsTheoretical;
  const card = h("div", { cls: "card", testid: "cm-avt" }, [
    h("h2", {}, [
      "Actual vs theoretical cost ",
      h("small", {
        text: `${report.meta.open ? "Not final yet. " : ""}From the same books as item usage`,
      }),
    ]),
  ]);
  const box = (k: string, v: number, d: string, cls = "", testid = ""): HTMLElement =>
    h("div", { cls: `box${cls === "" ? "" : ` ${cls}`}`, testid }, [
      h("div", { cls: "k", text: k }),
      h("div", { cls: "v", text: formatMoney(v) }),
      h("div", { cls: "d", text: d }),
    ]);
  card.append(
    h("div", { cls: "eq" }, [
      box(
        "Theoretical",
        a.theoreticalCents,
        "Cost of every drink made, by recipe",
        "",
        "cm-avt-theoretical",
      ),
      h("span", { cls: "op", text: "+" }),
      box("Waste", a.wasteCents, "Spills, breakage, over-pours", "", "cm-avt-waste"),
      h("span", { cls: "op", text: "+" }),
      box(
        "Count shortfall",
        a.shortfallCents,
        "What counts found missing, net",
        "",
        "cm-avt-shortfall",
      ),
      h("span", { cls: "op", text: "=" }),
      box("Actual", a.actualCents, "Cost of stock that left the shelf", "sum", "cm-avt-actual"),
    ]),
  );
  const gap = a.gapCents;
  const gapText =
    gap === 0
      ? "Actual is the same as theoretical."
      : `Actual is ${formatMoney(Math.abs(gap))} ${gap > 0 ? "more" : "less"} than theoretical${a.gapBp === null ? "" : `, ${percentText(Math.abs(a.gapBp))}`}.`;
  card.append(
    h("div", { cls: "strip", testid: "cm-avt-line" }, [
      h("span", {}, [
        h("b", { text: gapText }),
        ` Counts in these dates covered ${a.itemsCounted} of ${a.itemsTotal} stock items, so the shortfall only reflects those. ` +
          (a.itemsWithoutCost > 0
            ? `${a.itemsWithoutCost} counted ${plural(a.itemsWithoutCost, "item has", "items have")} no cost yet and ${plural(a.itemsWithoutCost, "is", "are")} left out. `
            : "") +
          "Pick dates that start and end on a full count for the whole picture.",
      ]),
    ]),
  );
  card.append(
    h("div", { cls: "inline-cta cm-left" }, [
      button(
        view.tieOpen ? "Hide how this ties" : "Show how this ties to item usage",
        "btn-quiet",
        "cm-tie-toggle",
        handlers.onToggleTie,
      ),
    ]),
  );
  if (view.tieOpen) {
    const t = a.tie;
    const row = (label: string, small: string, value: string): HTMLElement =>
      h("div", { cls: "r" }, [
        h("span", {}, [label, ...(small === "" ? [] : [h("small", { text: small })])]),
        h("b", { text: value }),
      ]);
    const rows: HTMLElement[] = [
      row(
        "Cost of drinks sold in these dates",
        "The Cost card above. Each sale carries the cost of its own drinks.",
        formatMoney(t.matchedCents),
      ),
      row(
        "Plus drinks made in these dates and not paid in them",
        `${t.madeNotPaidLines} ${plural(t.madeNotPaidLines, "drink", "drinks")} on tabs paid after the last date or still open`,
        `+ ${formatMoney(t.madeNotPaidCents)}`,
      ),
      row(
        "Plus drinks made, then taken off the ticket",
        "Removed or voided after they were made. The stock was used, nothing was sold.",
        `+ ${formatMoney(t.madeRemovedCents)}`,
      ),
      row(
        "Minus drinks made before these dates and paid in them",
        "Tabs that crossed the start date",
        `- ${formatMoney(t.madeEarlierCents)}`,
      ),
    ];
    if (t.madeLaterCents !== 0) {
      rows.push(
        row(
          "Minus drinks made after these dates and paid in them",
          "Made again after the last date on a tab paid inside it",
          `- ${formatMoney(t.madeLaterCents)}`,
        ),
      );
    }
    if (t.leftOutCents !== 0) {
      rows.push(
        row(
          "Minus cost on items shown without a total",
          "Items with a part that has no cost on some entry, or no longer on your list",
          `- ${formatMoney(t.leftOutCents)}`,
        ),
      );
    }
    if (t.roundingCents !== 0) {
      rows.push(
        row(
          "Rounding",
          "Each item is rounded once, each drink and day once here",
          `${t.roundingCents > 0 ? "+" : "-"} ${formatMoney(Math.abs(t.roundingCents))}`,
        ),
      );
    }
    rows.push(
      row("Theoretical cost, which is Used cost on items", "", formatMoney(t.usedCents)),
    );
    card.append(h("div", { cls: "cm-tie", testid: "cm-tie" }, rows));
  }
  card.append(
    h("div", { cls: "memo" }, [
      h("span", {}, [
        "Theoretical is what the recipes say was poured, priced at each ingredient's cost when the drink was made. Waste and count shortfall come from item usage. Which item is being over-poured is on the Recorded closed-count comparison on the Reports home for the same dates, because it has to compare each bottle between two counts.",
      ]),
    ]),
  );
  return card;
}

function listCard(
  drinks: CostMarginDrinkDto[],
  view: CostMarginView,
  handlers: CostMarginHandlers,
  kind: "partial" | "left",
): HTMLElement {
  const sorted = [...drinks].sort(
    (a, b) =>
      (kind === "left" ? b.salesCents - a.salesCents : b.leftOutSalesCents - a.leftOutSalesCents) ||
      a.name.localeCompare(b.name),
  );
  const shown = view.showAllLists ? sorted : sorted.slice(0, LIST_ROWS_SHOWN);
  const total = drinks.reduce(
    (n, d) => n + (kind === "left" ? d.salesCents : d.leftOutSalesCents),
    0,
  );
  const body = h("tbody");
  for (const d of shown) {
    const why =
      kind === "left"
        ? whyLeftOut(d)
        : {
            bold: "No cost yet",
            rest:
              d.missingParts.length > 0
                ? ` on ${d.missingParts.join(", ")}. Costed from the parts that have a price.`
                : ". Some sales had no priced part.",
          };
    body.append(
      h("tr", { testid: kind === "left" ? "cm-left-row" : "cm-partial-row" }, [
        h("td", { cls: "l rl" }, [
          h("div", { cls: "dlab" }, [d.name, h("small", { text: categoryName(d.category) })]),
        ]),
        h("td", { text: String(d.sold) }),
        h("td", { cls: "strong", text: formatMoney(d.salesCents) }),
        h("td", { cls: "why" }, [h("b", { text: why.bold }), why.rest]),
      ]),
    );
  }
  const card = h(
    "div",
    { cls: "card", testid: kind === "left" ? "cm-left-list" : "cm-partial-list" },
    [
      h("h2", {}, [
        kind === "left" ? "Left out for no cost " : "Partly costed ",
        h("small", {
          text:
            kind === "left"
              ? `${drinks.length} ${plural(drinks.length, "drink", "drinks")}, ${formatMoney(total)} of sales`
              : `${drinks.length} ${plural(drinks.length, "drink", "drinks")}, costed from the parts that have a price`,
        }),
      ]),
      h("div", { cls: "tblwrap" }, [
        h("table", { cls: "left cm-list-table" }, [
          h("thead", {}, [
            h("tr", {}, [
              h("th", { cls: "l", text: "Drink" }),
              h("th", { text: "Sold" }),
              h("th", { text: "Sales" }),
              h("th", { cls: "st", text: "Why" }),
            ]),
          ]),
          body,
        ]),
      ]),
    ],
  );
  if (!view.showAllLists && sorted.length > LIST_ROWS_SHOWN) {
    card.append(
      h("div", { cls: "showall" }, [
        button(
          `Show all ${sorted.length} drinks`,
          "btn-quiet",
          kind === "left" ? "cm-show-all-left" : "cm-show-all-partial",
          handlers.onShowAllLists,
        ),
      ]),
    );
  }
  card.append(
    h("div", { cls: "memo" }, [
      h("span", {
        text:
          kind === "left"
            ? "A part gets its cost at its next delivery that has a price. Drinks made from it after that are costed. Drinks already sold stay without a cost, so this list never shows a guess."
            : "Their cost is a little low until every part has a price. They are in cost, margin and pour cost, and tagged Partly costed in the drink list.",
      }),
    ]),
  );
  return card;
}
